import api, { API } from "@/lib/api";

/**
 * Helpers for photos stored in Emergent Object Storage (via the backend /api/files endpoints).
 *
 * Report images are stored as [{ file_id, caption }]. Older reports may still hold data-URI strings;
 * `imageSrc()` handles both so the UI never breaks on legacy data.
 */

const token = () => localStorage.getItem("vms_token") || "";

/** URL usable directly in <img src>. <img> cannot send headers, so the JWT rides along as ?auth=. */
export function fileUrl(fileId, { size = "full" } = {}) {
  if (!fileId) return "";
  const params = new URLSearchParams();
  if (size === "thumb") params.set("size", "thumb");
  params.set("auth", token());
  return `${API}/files/${fileId}?${params.toString()}`;
}

/** Best <img src> for a report image item (new object form or legacy data-URI string). */
export function imageSrc(item, { size = "thumb" } = {}) {
  if (!item) return "";
  if (typeof item === "string") return item; // legacy data-URI
  if (item.file_id) return fileUrl(item.file_id, { size });
  if (item.id) return fileUrl(item.id, { size });
  return item.thumb_url || item.url || "";
}

export const imageCaption = (item) => (item && typeof item === "object" ? item.caption || "" : "");

/**
 * Upload a single image. Resolves to the backend file record
 * ({ id, url, thumb_url, width, height, caption, ... }).
 */
export async function uploadImage(file, { kind = "report", refId, caption, onProgress } = {}) {
  const fd = new FormData();
  fd.append("file", file, file.name || "foto.jpg");
  fd.append("kind", kind);
  if (refId) fd.append("ref_id", refId);
  if (caption) fd.append("caption", caption);
  const res = await api.post("/files/upload", fd, {
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress: (e) => {
      if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100));
    },
  });
  return res.data;
}

/** Map uploader state -> API payload (only finished uploads). */
export const toImageRefs = (items) =>
  (items || [])
    .filter((i) => i && i.file_id && i.status !== "uploading" && i.status !== "error")
    .map((i) => ({ file_id: i.file_id, caption: (i.caption || "").trim() }));

export const hasPendingUploads = (items) => (items || []).some((i) => i.status === "uploading");

/** Open the full-size image in a new tab. */
export function openImage(item) {
  const src = imageSrc(item, { size: "full" });
  if (src) window.open(src, "_blank", "noopener");
}
